import "./styles/app.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { env } from "./lib/env.ts";
import { queryClient } from "./lib/query-client.ts";
import { routeTree } from "./routeTree.gen.ts";

document.documentElement.classList.add("light");

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { queryClient },
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

async function enableMocking(): Promise<void> {
  if (env.isDev && env.apiMock) {
    const { startMockWorker } = await import("./mocks/browser.ts");
    await startMockWorker();
  }
}

function ApiModeBanner() {
  if (!env.apiMock) return null;

  return (
    <div
      data-testid="api-mode-banner"
      role="status"
      style={{
        position: "fixed",
        top: 8,
        right: 8,
        zIndex: 9999,
        border: "1px solid #fed7aa",
        borderRadius: 999,
        background: "#fff7ed",
        color: "#9a3412",
        padding: "6px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      Local demo mode — MSW mock API enabled with VITE_API_MOCK=1
    </div>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

void enableMocking().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ApiModeBanner />
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
